/**
 * ObjectsVisualizer - Main component for visualizing Cypher query results as a graph.
 *
 * The view is driven by derived atoms:
 * - VisibleNodesAtom / VisibleEdgesAtom compute the graph from base + focus + exploration state
 * - Event handlers dispatch write atoms (actions) that update source atoms
 * - No imperative useEffect chains — the graph is always a function of state
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
	ReactFlow,
	ReactFlowProvider,
	useNodesState,
	useEdgesState,
	useReactFlow,
	useStoreApi,
	Controls,
	Background,
	BackgroundVariant,
	type NodeTypes,
	type EdgeTypes,
	type Node,
	type Edge,
} from '@xyflow/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- ReactFlow requires its base CSS
import '@xyflow/react/dist/style.css';
import { bind } from 'bind-event-listener';
import { useAtomValue, useSetAtom } from 'jotai';

import { getDocument } from '@atlaskit/browser-apis';
import { css, cssMap, jsx } from '@atlaskit/css';
import Flag from '@atlaskit/flag/flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../../../../common/analytics/generated/use-analytics-events';
import { useAtlassianUserContext } from '../../../../../hooks/use-atlassian-user';
import { ObjectsQueryResultAtom, ObjectsQueryRunningAtom } from '../../../../state/LabsAtoms';

// Actions
import {
	EnterFocusModeAtom,
	ExitFocusModeAtom,
	ResetExplorationAtom,
} from './actions/focusActions';
import { DrillIntoChildAtom, GoBackToFocusedAtom } from './actions/navigationActions';
// Atoms
import { BaseNodesAtom, BaseEdgesAtom, BaseNodeIdsAtom } from './atoms/baseGraphAtoms';
import { ExploredNodeArisAtom } from './atoms/cacheAtoms';
import { VisibleNodesAtom, VisibleEdgesAtom } from './atoms/derivedAtoms';
import { ErrorFlagsAtom } from './atoms/explorationAtoms';
import { FocusedNodeAtom, IsFocusModeAtom, EffectiveExplorationNodeAtom } from './atoms/focusAtoms';
import { ActionHistoryAtom, RedoStackAtom, UndoAtom } from './atoms/historyAtoms';
import {
	InitRadialLayoutAtom,
	ExpandNodeAtom,
	ExpandedNodeIdsAtom,
	ExpandingNodeIdsAtom,
	LayoutNodesAtom,
	LayoutStatusAtom,
	ResetLayoutAtom,
	estimateLayoutSize,
} from './atoms/layoutAtoms';
import { TRANSITION_MS, LeftPanelExpandedAtom } from './atoms/panelAtoms';
import { StampRecencyAtom, ClearHiddenNodesAtom } from './atoms/recencyAtoms';
import {
	ExpandedGroupsAtom,
	LastExpandedGroupAtom,
	ResetExpandedGroupsAtom,
	ViewportSizeAtom,
	ForceLayoutPositionsAtom,
} from './atoms/visualizerAtoms';
import { ExplorationContext, type ExplorationContextType } from './context/ExplorationContext';
import {
	GraphSelectionContext,
	computeNodeZIndex,
	type GraphSelectionState,
} from './context/GraphSelectionContext';
import { useRadialMagneticLayout } from './hooks/useRadialMagneticLayout';
import { computePullInTargets } from './layout/MagneticDynamics';
import { FloatingEdge } from './nodes/FloatingEdge';
import { LoadingNode } from './nodes/LoadingNode';
import { NoResultsHeaderNode } from './nodes/NoResultsHeaderNode';
import { ObjectEdge, type ObjectEdgeData } from './nodes/ObjectEdge';
import { ObjectNode, type ObjectNodeData } from './nodes/ObjectNode';
import { UserObjectNode } from './nodes/UserObjectNode';
import { CURRENT_USER_ARI_PROXY } from './services/cypherQueryService';
import { useCypherExplorationGraph } from './useCypherExplorationGraph';
import { useObjectsGraph } from './useObjectsGraph';

const nodeTypes: NodeTypes = {
	objectNode: ObjectNode,
	userObjectNode: UserObjectNode,
	noResultsHeaderNode: NoResultsHeaderNode,
	loadingNode: LoadingNode,
};

const edgeTypes: EdgeTypes = {
	objectEdge: ObjectEdge,
	floatingEdge: FloatingEdge,
};

const testModeClickedNodeTestIds: string[] = [];

const isGraphExplorerTestModeEnabled = (): boolean =>
	typeof window !== 'undefined' && window.location.hash === '#test-mode';

const recordGraphExplorerTestModeNodeClick = (node: Node): void => {
	if (!isGraphExplorerTestModeEnabled()) {
		return;
	}

	testModeClickedNodeTestIds.push(`data-testid="rf__node-${node.id}"`);
	// eslint-disable-next-line no-console -- Test-mode recorder intentionally prints the click path for manual testing.
	console.log(testModeClickedNodeTestIds.join(' -> '));
};

const GRAPH_BACKGROUND_COLOR = 'var(--Primary-Black, #101214)';

const containerStyles = css({
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- dark theme override
	backgroundColor: GRAPH_BACKGROUND_COLOR as any,
	borderRadius: token('radius.medium'),
	overflow: 'hidden',
});

const GRAPH_DOT_COLOR = 'rgba(255,255,255,0.06)';
const GRAPH_DOT_GAP = 30;
const GRAPH_DOT_SIZE = 1;
const REACT_FLOW_PRO_OPTIONS = { hideAttribution: true };

const styles = cssMap({
	graphCanvas: {
		// @ts-expect-error -- custom CSS property not in cssMap's known property list
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- ReactFlow reads this custom property for the canvas background
		'--xy-background-color': GRAPH_BACKGROUND_COLOR as any,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- ReactFlow internal class; cannot be targeted another way
		'& .react-flow__edge': {
			pointerEvents: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- ReactFlow internal class; cannot be targeted another way
		'& .react-flow__edge-path': {
			pointerEvents: 'none',
		},
	},
	graphViewport: {
		width: '100%',
		height: '100%',
	},
});

// Stable node/edge arrays for the loading and no-results ReactFlow instances.
// These MUST be module-level constants (not created inline in the render body)
// because ReactFlow v12 starts nodes with `visibility: hidden` until it
// measures their dimensions. If we create a new array on every render,
// ReactFlow treats them as "new unmeasured" nodes and reapplies
// `visibility: hidden`, causing the card to flash and then disappear.
const LOADING_NODES: Node<any>[] = [
	{
		id: 'loading',
		type: 'loadingNode',
		position: { x: 0, y: 0 },
		data: { _tag: 'loading' },
		selectable: false,
		draggable: false,
		connectable: false,
	},
];

const EMPTY_EDGES: Edge<any>[] = [];

interface ObjectsVisualizerInnerProps {
	onExplorationStart: () => void;
	/** Whether the left (explorer) floating panel is currently visible. */
	showLeftPanel?: boolean;
	/** Width of the left floating panel in pixels. */
	leftPanelWidth?: number;
	/** Ref to the outer container div, used to measure the effective viewport. */
	containerRef: React.RefObject<HTMLDivElement | null>;
}

function ObjectsVisualizerInner({
	onExplorationStart,
	showLeftPanel = false,
	leftPanelWidth = 0,
	containerRef,
}: ObjectsVisualizerInnerProps) {
	const queryResult = useAtomValue(ObjectsQueryResultAtom);
	const isRunning = useAtomValue(ObjectsQueryRunningAtom);
	const { identity: currentUser, loading: currentUserLoading } = useAtlassianUserContext();

	// Read state from atoms
	const isFocusMode = useAtomValue(IsFocusModeAtom);
	const focusedNode = useAtomValue(FocusedNodeAtom);
	const activeExplorationNode = useAtomValue(EffectiveExplorationNodeAtom);
	const exploredNodeAris = useAtomValue(ExploredNodeArisAtom);
	const baseNodeIds = useAtomValue(BaseNodeIdsAtom);

	// Derived graph state — replaces the old useEffect chains
	const visibleNodes = useAtomValue(VisibleNodesAtom);
	const visibleEdges = useAtomValue(VisibleEdgesAtom);

	// Expand/collapse state for result groups
	const expandedGroups = useAtomValue(ExpandedGroupsAtom);
	const lastExpandedGroup = useAtomValue(LastExpandedGroupAtom);
	const resetExpandedGroups = useSetAtom(ResetExpandedGroupsAtom);
	const setLastExpandedGroup = useSetAtom(LastExpandedGroupAtom);

	// Layout force flag (shared between effects)
	const setForceLayout = useSetAtom(ForceLayoutPositionsAtom);

	// History: current state for snapshots and push/clear
	const setActionHistory = useSetAtom(ActionHistoryAtom);
	const setRedoStack = useSetAtom(RedoStackAtom);
	const undo = useSetAtom(UndoAtom);

	// Analytics
	const { fireEvent } = useAnalyticsEvents();

	// Actions
	const enterFocusMode = useSetAtom(EnterFocusModeAtom);
	const exitFocusMode = useSetAtom(ExitFocusModeAtom);
	const resetExploration = useSetAtom(ResetExplorationAtom);
	const drillIntoChild = useSetAtom(DrillIntoChildAtom);
	const goBackToFocusedAtom = useSetAtom(GoBackToFocusedAtom);
	const setLeftPanelExpanded = useSetAtom(LeftPanelExpandedAtom);
	const setBaseNodes = useSetAtom(BaseNodesAtom);
	const setBaseEdges = useSetAtom(BaseEdgesAtom);

	// Transform query results to graph data
	const graphData = useObjectsGraph(queryResult);

	// ========================================================================
	// Simplified grouped graph: fetch all relationships upfront and display
	// as grouped nodes with a D3 Force layout.
	// ========================================================================

	const userNodeData = useMemo<ObjectNodeData>(
		() => ({
			id: CURRENT_USER_ARI_PROXY,
			typename: 'AtlassianUser',
			displayName: 'You',
			url: null,
			properties: currentUser.id
				? {
					bootstrapUser: {
						id: currentUser.id,
						displayName: currentUser.name || currentUser.nickname || 'You',
						email: currentUser.email,
						picture: currentUser.picture,
					},
				}
				: {},
		}),
		[
			currentUser.email,
			currentUser.id,
			currentUser.name,
			currentUser.nickname,
			currentUser.picture,
		],
	);
	const rootLayoutNodes = useAtomValue(LayoutNodesAtom);
	const rootNodeId = useMemo(() => {
		for (const [nodeId, layoutNode] of rootLayoutNodes) {
			if (!layoutNode.parentId) {
				return nodeId;
			}
		}
		return null;
	}, [rootLayoutNodes]);

	// Trigger cypher exploration for the user node once it's available
	const initRadialLayout = useSetAtom(InitRadialLayoutAtom);
	const resetRadialLayout = useSetAtom(ResetLayoutAtom);
	const collaboratorStatus = useAtomValue(LayoutStatusAtom);
	const discoveryTriggeredRef = useRef<string | null>(null);

	useEffect(() => {
		if (currentUserLoading && !userNodeData.properties.bootstrapUser) {
			return;
		}
		// Only trigger discovery once per user node
		if (discoveryTriggeredRef.current === userNodeData.id) {
			return;
		}
		// Reset layout atoms when the user node changes (e.g. site switch)
		if (discoveryTriggeredRef.current !== null) {
			resetRadialLayout();
		}
		discoveryTriggeredRef.current = userNodeData.id;
		initRadialLayout(userNodeData);
	}, [currentUserLoading, userNodeData, initRadialLayout, resetRadialLayout]);

	// Build the cypher exploration graph (replaces collaborators graph as data source)
	const collaboratorsGraph = useCypherExplorationGraph(userNodeData);

	// ReactFlow local state — synced from derived atoms
	// Uses `any` for node data since we have multiple node types (object, group, header)
	const [nodes, setNodes, onNodesChange] = useNodesState<Node<any>>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<ObjectEdgeData>>([]);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const {
		fitView,
		setCenter: _setCenter,
		setViewport,
		getViewport: _getViewport,
		getZoom,
		getNodes,
	} = useReactFlow();

	// Panel inset (must match PANEL_INSET in index.tsx).
	// Each floating panel is offset from the container edge by this inset,
	// and the content behind it is also occluded by the same margin on
	// the panel's inner side (between the panel edge and the graph area).
	const PANEL_INSET_PX = 12;

	/**
	 * Computes the effective viewport dimensions and offsets,
	 * subtracting the floating panel areas from the total canvas.
	 *
	 * Each visible panel occludes: inset + panelWidth + inset
	 * (one inset for the gap between the container edge and the panel,
	 *  one for the gap between the panel's inner edge and the graph content).
	 */
	const getEffectiveViewport = useCallback(() => {
		const containerEl = containerRef.current;
		const containerW = containerEl?.clientWidth ?? 1000;
		const containerH = containerEl?.clientHeight ?? 600;

		// Each panel occludes its width plus insets on both sides
		const leftOccupied = showLeftPanel ? leftPanelWidth + PANEL_INSET_PX * 2 : 0;

		const effectiveW = containerW - leftOccupied;
		const effectiveLeft = leftOccupied;

		return {
			containerW,
			containerH,
			effectiveW: Math.max(effectiveW, 200), // floor to prevent negative
			effectiveH: containerH,
			effectiveLeft,
		};
	}, [showLeftPanel, leftPanelWidth, containerRef]);

	/**
	 * Smart fitView for relationship additions: only resets zoom if the
	 * current zoom is too high to accommodate all nodes. Otherwise, just
	 * pans to center the content within the effective (non-occluded) viewport.
	 *
	 * Uses a fixed pixel padding (FIT_VIEW_PADDING_PX) instead of a
	 * proportional percentage so the breathing room around the graph stays
	 * constant regardless of viewport size. This avoids the previous issue
	 * where 15% proportional padding on both axes created increasingly
	 * large dead zones at bigger viewport sizes, making the graph appear
	 * to be zoomed out much further than necessary.
	 */
	const FIT_VIEW_PADDING_PX = 40;

	const smartFitView = useCallback(
		(opts?: { duration?: number }) => {
			const duration = opts?.duration ?? 300;
			const allNodes = getNodes();

			// In focus mode, only consider fully visible nodes (opacity === 1 or
			// no explicit opacity). This excludes the dimmed base/result nodes
			// (opacity: 0.12) that are faded in the background.
			const currentNodes = isFocusMode
				? allNodes.filter((node) => {
					const opacity = (node.style as any)?.opacity;
					return opacity === undefined || opacity === 1;
				})
				: allNodes;

			if (currentNodes.length === 0) {
				return;
			}

			// Calculate bounding box of visible (non-dimmed) nodes
			let minX = Infinity,
				minY = Infinity,
				maxX = -Infinity,
				maxY = -Infinity;
			for (const node of currentNodes) {
				const w = node.measured?.width ?? 220;
				const h = node.measured?.height ?? 60;
				minX = Math.min(minX, node.position.x);
				minY = Math.min(minY, node.position.y);
				maxX = Math.max(maxX, node.position.x + w);
				maxY = Math.max(maxY, node.position.y + h);
			}

			const graphWidth = maxX - minX;
			const graphHeight = maxY - minY;

			const { effectiveW, effectiveH, effectiveLeft } = getEffectiveViewport();

			// Use fixed pixel padding on each side so the graph gets consistent
			// breathing room without wasting large chunks of the viewport.
			const availableW = effectiveW - FIT_VIEW_PADDING_PX * 2;
			const availableH = effectiveH - FIT_VIEW_PADDING_PX * 2;

			const currentZoom = getZoom();
			const fitsAtCurrentZoom =
				graphWidth * currentZoom <= availableW && graphHeight * currentZoom <= availableH;

			if (fitsAtCurrentZoom) {
				// Zoom is fine — just pan to center content in the effective viewport
				const graphCenterX = minX + graphWidth / 2;
				const graphCenterY = minY + graphHeight / 2;

				const effectiveCenterX = effectiveLeft + effectiveW / 2;
				const effectiveCenterY = effectiveH / 2;

				const newX = effectiveCenterX - graphCenterX * currentZoom;
				const newY = effectiveCenterY - graphCenterY * currentZoom;

				setViewport({ x: newX, y: newY, zoom: currentZoom }, { duration });
			} else {
				// Need to zoom out — calculate the right zoom to fit everything
				// in the effective viewport, then center.
				const zoomW = availableW / graphWidth;
				const zoomH = availableH / graphHeight;
				const newZoom = Math.min(zoomW, zoomH, 1); // cap at 1x

				const graphCenterX = minX + graphWidth / 2;
				const graphCenterY = minY + graphHeight / 2;

				const effectiveCenterX = effectiveLeft + effectiveW / 2;
				const effectiveCenterY = effectiveH / 2;

				const newX = effectiveCenterX - graphCenterX * newZoom;
				const newY = effectiveCenterY - graphCenterY * newZoom;

				setViewport({ x: newX, y: newY, zoom: newZoom }, { duration });
			}
		},
		[getNodes, getZoom, setViewport, getEffectiveViewport, isFocusMode],
	);

	// Track the previous node count so we only fitView when new nodes are added
	const prevNodeCountRef = useRef(0);

	// Track queryResult and expandedGroups identity so we can distinguish
	// "new query", "expand/collapse toggle", and "viewport-only resize".
	const prevQueryResultRef = useRef(queryResult);
	const prevExpandedGroupsRef = useRef(expandedGroups);

	// ========================================================================
	// Reactive fitView / recalculate — replaces fragile setTimeout chains.
	//
	// Instead of guessing timing with setTimeout, we record *intent* (what to
	// fit, whether to recalculate group sizes) in refs. When ReactFlow reports
	// that it has finished measuring node dimensions (via `dimensions`-type
	// changes in onNodesChange), we execute the pending operations. This
	// guarantees the DOM is measured before we fitView.
	// ========================================================================
	type PendingFitView =
		| { type: 'all'; padding: number; duration: number }
		| {
			type: 'group';
			typename: string;
			graphDataNodes: Node<any>[];
			padding: number;
			duration: number;
		};

	const pendingFitViewRef = useRef<PendingFitView | null>(null);
	const pendingRecalcRef = useRef(false);
	// Debounce raf id for batching rapid dimension change callbacks
	const dimensionRafRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);

	// Sync base graph atoms when graphData changes (new query OR expand/collapse)
	useEffect(() => {
		const isNewQuery = queryResult !== prevQueryResultRef.current;
		const isExpandChange = expandedGroups !== prevExpandedGroupsRef.current;
		prevQueryResultRef.current = queryResult;
		prevExpandedGroupsRef.current = expandedGroups;

		if (graphData.nodes.length === 0 && graphData.edges.length === 0) {
			// Fire results view analytics for empty/error results on new queries
			if (isNewQuery && queryResult !== undefined) {
				// const hasError = queryResult?.error != null;
				// const status = hasError ? 'error' : 'no_results';
				// fireEvent('screen.gcResults.viewed', { status });
			}
			setBaseNodes([]);
			setBaseEdges([]);
			setNodes([]);
			setEdges([]);
			prevNodeCountRef.current = 0;
			resetExpandedGroups();
			return;
		}

		// Viewport-only resize: update layout positions and fit the view,
		// but skip focus/exploration resets so user state is preserved.
		// In focus mode the background is faded and non-interactive — skip
		// repositioning entirely to avoid jarring visual movement.
		const isViewportOnly = !isNewQuery && !isExpandChange;
		if (isViewportOnly) {
			if (isFocusMode) {
				return;
			}
			setBaseNodes(graphData.nodes);
			setBaseEdges(graphData.edges);
			setForceLayout(true);
			// Record intent — fitView will execute reactively once ReactFlow
			// reports dimension changes in handleNodesChangeSafe.
			pendingFitViewRef.current = { type: 'all', padding: 0.2, duration: 200 };
			return;
		}

		setBaseNodes(graphData.nodes);
		setBaseEdges(graphData.edges);

		// Force layout positions so the sync effect picks up the new masonry
		// positions instead of preserving stale drag positions.
		setForceLayout(true);

		if (isNewQuery) {
			if (isFocusMode) {
				exitFocusMode();
			}
			resetExploration();
			resetExpandedGroups();
			// Preserve executeQuery history entries so users can undo back
			// to the explore landing state. Clear all other (focus-mode) entries.
			setActionHistory((prev) => prev.filter((entry) => entry.action.type === 'executeQuery'));
			setRedoStack((prev) => prev.filter((action) => action.type === 'executeQuery'));

			const hasError = queryResult?.error != null;
			const hasNodes = graphData.nodes.length > 0;
			const status = hasError ? 'error' : hasNodes ? 'success' : 'no_results';
			// fireEvent('screen.gcResults.viewed', { status });
			void status;

			// Record intent — fitView will execute reactively once ReactFlow
			// reports dimension changes in handleNodesChangeSafe.
			pendingFitViewRef.current = { type: 'all', padding: 0.2, duration: 200 };
		} else {
			// Expand/collapse toggle — center on the expanded group's nodes
			// so the user sees the new content, or fit-all on collapse.
			const expandedTypename = lastExpandedGroup;
			setLastExpandedGroup(null);

			if (expandedTypename) {
				// Record intent to fitView on the specific expanded group.
				// The actual fitView will fire reactively once ReactFlow has
				// measured the new nodes, guaranteeing accurate dimensions.
				pendingFitViewRef.current = {
					type: 'group',
					typename: expandedTypename,
					graphDataNodes: graphData.nodes,
					padding: 0.2,
					duration: 200,
				};
			} else {
				// Collapse — fit all nodes once dimensions settle.
				pendingFitViewRef.current = { type: 'all', padding: 0.2, duration: 200 };
			}
		}
	}, [graphData]); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync ReactFlow state from derived atoms, preserving user-dragged positions,
	// selection state, and measured dimensions across layout recomputes.
	//
	// ReactFlow stores internal measurement data (width, height, measured) on
	// node objects after it measures the DOM. Completely overwriting nodes with
	// fresh atom objects loses these dimensions, causing nodes to disappear
	// (they collapse to 0×0 until ReactFlow re-measures them, which may not
	// happen reliably). We preserve these properties from the existing ReactFlow
	// nodes and merge them into the new atom-derived nodes.
	const forceLayout = useAtomValue(ForceLayoutPositionsAtom);
	useEffect(() => {
		const shouldForceLayout = forceLayout;
		if (shouldForceLayout) {
			setForceLayout(false);
		}

		// Build maps of current ReactFlow node state so we can preserve
		// positions, selection, and — critically — measured dimensions.
		const currentRfNodes = getNodes();
		const currentRfNodeMap = new Map<string, (typeof currentRfNodes)[number]>();
		for (const rfNode of currentRfNodes) {
			currentRfNodeMap.set(rfNode.id, rfNode);
		}

		// Merge positions, selection, and dimensions:
		// - Group/header nodes: always use atom position (they follow the layout)
		// - When forceLayout is set (new query or expand/collapse): use atom
		//   positions for all nodes so the masonry layout takes full effect.
		// - In focus mode: always use atom (dagre) positions for ALL object nodes,
		//   because dagre re-lays out the entire exploration graph whenever
		//   relationships change. Preserving old positions would cause overlaps.
		// - Outside focus mode: preserve user-dragged positions for base nodes.
		// - Always: preserve ReactFlow's selected state so selection isn't lost
		//   when nodes are replaced during viewport resize or layout recompute.
		// - Always: preserve ReactFlow's measured dimensions (width, height,
		//   measured) so nodes don't disappear after re-render.
		const mergedNodes = visibleNodes.map((atomNode) => {
			const oldNode = currentRfNodeMap.get(atomNode.id);
			const isSelected = oldNode?.selected;
			const selectedPatch = isSelected ? { selected: true } : {};

			// Preserve measured dimensions from ReactFlow's internal state,
			// but ONLY for object nodes. Group and header nodes manage their
			// own sizing through data.width / data.height and must not inherit
			// stale measured dimensions from a previous layout (e.g. carrying
			// expanded-state measurements into a collapsed group would make the
			// group rectangle too large).
			const dimensionPatch: Record<string, any> = {};
			if (oldNode && atomNode.type === 'objectNode') {
				if (oldNode.measured) {
					dimensionPatch.measured = oldNode.measured;
				}
				if (oldNode.width != null) {
					dimensionPatch.width = atomNode.width ?? oldNode.width;
				}
				if (oldNode.height != null) {
					dimensionPatch.height = atomNode.height ?? oldNode.height;
				}
			}

			if (atomNode.type !== 'objectNode') {
				return { ...atomNode, ...selectedPatch };
			}
			if (shouldForceLayout || isFocusMode) {
				return { ...atomNode, ...dimensionPatch, ...selectedPatch };
			}
			const existingPos = oldNode?.position;
			if (existingPos) {
				return {
					...atomNode,
					position: existingPos,
					...dimensionPatch,
					...selectedPatch,
				};
			}
			return { ...atomNode, ...dimensionPatch, ...selectedPatch };
		});

		// For edges, merge with existing ReactFlow edge state to preserve
		// any internal properties (e.g. animation state, style overrides).
		const currentRfEdges = edges;
		const currentEdgeMap = new Map(currentRfEdges.map((e) => [e.id, e]));
		const mergedEdges = visibleEdges.map((atomEdge) => {
			const oldEdge = currentEdgeMap.get(atomEdge.id);
			if (!oldEdge) {
				return atomEdge;
			}
			return {
				...atomEdge,
				// Preserve visual properties from the old edge that ReactFlow
				// may have set internally
				animated: atomEdge.animated ?? oldEdge.animated,
			};
		});

		setNodes(mergedNodes);
		setEdges(mergedEdges);

		// After a forced layout (expand/collapse or new query), the atom-derived
		// group nodes have dimensions based on ASSUMED_NODE_HEIGHT (70px). If
		// smart-link cards have already been measured and are taller, the groups
		// will be too small. Mark a pending recalculation so that when ReactFlow
		// reports dimension changes in handleNodesChangeSafe, we recalculate
		// group sizes with accurate measured data before executing any pending
		// fitView.
		if (shouldForceLayout && nodeDimensionsRef.current.size > 0) {
			// Prune stale entries from the dimension cache: after a collapse,
			// some nodes are no longer visible and their old (potentially large)
			// measurements would cause recalculateGroupSizes to incorrectly
			// enlarge the collapsed group.
			const visibleNodeIds = new Set(visibleNodes.map((n) => n.id));
			for (const cachedId of nodeDimensionsRef.current.keys()) {
				if (!visibleNodeIds.has(cachedId)) {
					nodeDimensionsRef.current.delete(cachedId);
				}
			}

			pendingRecalcRef.current = true;
		}

		// Viewport-resize fallback: when no new nodes are being added and no
		// DOM sizes change, ReactFlow won't fire any `dimensions` changes in
		// onNodesChange. In that case the reactive path in handleNodesChangeSafe
		// never triggers. Detect this by checking whether every visible node
		// already existed in the previous ReactFlow state. If so, execute
		// pending operations directly via requestAnimationFrame (letting the
		// setNodes commit first).
		const hasNewNodes = visibleNodes.some((n) => !currentRfNodeMap.has(n.id));
		if (!hasNewNodes && (pendingFitViewRef.current || pendingRecalcRef.current)) {
			if (dimensionRafRef.current) {
				cancelAnimationFrame(dimensionRafRef.current);
			}
			dimensionRafRef.current = requestAnimationFrame(() => {
				dimensionRafRef.current = null;

				if (pendingRecalcRef.current) {
					pendingRecalcRef.current = false;
					recalculateGroupSizes();
				}

				const pending = pendingFitViewRef.current;
				if (pending) {
					pendingFitViewRef.current = null;
					if (pending.type === 'group') {
						const targetNodes = pending.graphDataNodes
							.filter((n) => {
								if (n.type === 'groupNode') {
									return n.id === `group-${pending.typename}`;
								}
								if (n.type === 'objectNode') {
									return (n.data as ObjectNodeData).typename === pending.typename;
								}
								return false;
							})
							.map((n) => ({ id: n.id }));
						fitView({
							nodes: targetNodes,
							padding: pending.padding,
							duration: pending.duration,
						});
					} else {
						fitView({ padding: pending.padding, duration: pending.duration });
					}
				}
			});
		}

		prevNodeCountRef.current = visibleNodes.length;
	}, [visibleNodes, visibleEdges, forceLayout]); // eslint-disable-line react-hooks/exhaustive-deps

	// Center on focused node when focus changes.
	// Wait for panel transitions to settle before recentering so
	// the ReactFlow container has its final dimensions.
	const transitionBuffer = TRANSITION_MS + 50;
	useEffect(() => {
		if (!focusedNode) {
			// Exiting focus mode → fit all nodes
			if (visibleNodes.length > 0) {
				// TODO: Replace setTimeout with ReactFlow's onNodesChange or
				// requestAnimationFrame for more reliable fitView timing.
				setTimeout(() => {
					fitView({ padding: 0.2, duration: 300 });
				}, transitionBuffer);
			}
			return;
		}

		// Entering focus mode: center on the focused node,
		// accounting for floating panel overlap.
		const node = visibleNodes.find((n) => n.id === focusedNode.id);
		if (!node) {
			return;
		}

		const currentZoom = getZoom();
		const nodeWidth = 220;
		const nodeHeight = 60;
		const nodeCenterX = node.position.x + nodeWidth / 2;
		const nodeCenterY = node.position.y + nodeHeight / 2;

		// TODO: Replace setTimeout with ReactFlow's onNodesChange or
		// requestAnimationFrame for more reliable setViewport timing.
		setTimeout(() => {
			const { effectiveW, effectiveH, effectiveLeft } = getEffectiveViewport();
			const effectiveCenterX = effectiveLeft + effectiveW / 2;
			const effectiveCenterY = effectiveH / 2;

			const newX = effectiveCenterX - nodeCenterX * currentZoom;
			const newY = effectiveCenterY - nodeCenterY * currentZoom;

			setViewport({ x: newX, y: newY, zoom: currentZoom }, { duration: 300 });
		}, transitionBuffer);
	}, [focusedNode?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	// When relationships are toggled (new exploration nodes added),
	// use smartFitView to reset the view intelligently.
	const prevVisibleNodeCountRef = useRef(visibleNodes.length);
	useEffect(() => {
		const prevCount = prevVisibleNodeCountRef.current;
		prevVisibleNodeCountRef.current = visibleNodes.length;

		// Only trigger when nodes are added during focus mode (exploration)
		if (isFocusMode && visibleNodes.length > prevCount && prevCount > 0) {
			// TODO: Replace setTimeout with ReactFlow's onNodesChange or
			// requestAnimationFrame for more reliable fitView timing.
			setTimeout(() => {
				smartFitView({ duration: 300 });
			}, transitionBuffer);
		}
	}, [visibleNodes.length]); // eslint-disable-line react-hooks/exhaustive-deps

	// ========================================================================
	// Event Handlers — thin wrappers that dispatch actions + record history
	// ========================================================================

	const handleGoBackToFocused = useCallback(() => {
		setLeftPanelExpanded(false);
		goBackToFocusedAtom();
	}, [setLeftPanelExpanded, goBackToFocusedAtom]);

	const handleFocus = useCallback(
		(nodeData: ObjectNodeData) => {
			const isBaseNode = baseNodeIds.has(nodeData.id);

			if (isFocusMode) {
				onExplorationStart();
				drillIntoChild(nodeData);
			} else if (isBaseNode) {
				onExplorationStart();
				enterFocusMode(nodeData);
			}
		},
		[onExplorationStart, enterFocusMode, isFocusMode, drillIntoChild, baseNodeIds],
	);

	const handleNodeClick = useCallback(
		(_event: React.MouseEvent, node: Node) => {
			// Ignore non-object nodes
			if (node.type !== 'objectNode') {
				return;
			}
			recordGraphExplorerTestModeNodeClick(node);
			// fireEvent('ui.node.clicked.gcResultsNode', {
			// 	type: (node?.data?.typename as string | undefined) ?? null,
			// });
			// No-op for selection — details panel removed from simple view
		},
		[],
	);

	// Double-click behaves identically to single-click: just "select" the
	// node (which in the microsite is a no-op beyond firing analytics).
	// Focus/drill/exploration modes are legacy and not used here.
	const handleNodeDoubleClick = handleNodeClick;

	const handleEdgeClick = useCallback((_event: React.MouseEvent, _edge: Edge<ObjectEdgeData>) => {
		// No-op — details panel removed from simple view
	}, []);

	// Handle pane clicks — use the native MouseEvent.detail to detect
	// double-clicks (detail === 2) for exiting focus mode.
	const handlePaneClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.detail === 2 && isFocusMode) {
				exitFocusMode();
			}
		},
		[isFocusMode, exitFocusMode],
	);

	// Compute sets of nodes with incoming/outgoing edges for directional indicators
	const { nodesWithIncomingEdges, nodesWithOutgoingEdges } = useMemo(() => {
		const incoming = new Set<string>();
		const outgoing = new Set<string>();
		for (const edge of edges) {
			if (edge.source) {
				outgoing.add(edge.source);
			}
			if (edge.target) {
				incoming.add(edge.target);
			}
		}
		return { nodesWithIncomingEdges: incoming, nodesWithOutgoingEdges: outgoing };
	}, [edges]);

	// Exploration context value
	const activeNodeAri = activeExplorationNode?.id ?? focusedNode?.id ?? null;
	const explorationContextValue = useMemo<ExplorationContextType>(
		() => ({
			onFocus: handleFocus,
			onExitFocus: exitFocusMode,
			onGoBackToFocused: handleGoBackToFocused,
			focusedNodeAri: focusedNode?.id ?? null,
			activeExplorationNodeAri: activeNodeAri,
			exploredNodeAris,
			nodesWithIncomingEdges,
			nodesWithOutgoingEdges,
		}),
		[
			handleFocus,
			exitFocusMode,
			handleGoBackToFocused,
			focusedNode?.id,
			activeNodeAri,
			exploredNodeAris,
			nodesWithIncomingEdges,
			nodesWithOutgoingEdges,
		],
	);

	// Track actual measured node dimensions to dynamically resize group containers
	const nodeDimensionsRef = useRef<Map<string, { width: number; height: number }>>(new Map());

	const recalculateGroupSizes = useCallback(() => {
		setNodes((currentNodes) => {
			// Collect object nodes and their measured dimensions
			const objectNodes = currentNodes.filter((n) => n.type === 'objectNode');
			if (objectNodes.length === 0) {
				return currentNodes;
			}

			// Group object nodes by typename
			const groupedByType = new Map<string, typeof objectNodes>();
			for (const node of objectNodes) {
				const typename = (node.data as ObjectNodeData).typename || 'Unknown';
				let group = groupedByType.get(typename);
				if (!group) {
					group = [];
					groupedByType.set(typename, group);
				}
				group.push(node);
			}

			let anyChanged = false;
			const updatedNodes = currentNodes.map((node) => {
				if (node.type !== 'groupNode') {
					return node;
				}

				const groupData = node.data as { typename: string; width: number; height: number };
				const typename = groupData.typename;
				const members = groupedByType.get(typename);
				if (!members || members.length === 0) {
					return node;
				}

				// Calculate bounding box of member nodes using measured dimensions
				const groupX = node.position.x;
				const groupY = node.position.y;
				let maxRight = 0;
				let maxBottom = 0;

				for (const member of members) {
					const measured = nodeDimensionsRef.current.get(member.id);
					const memberW = measured?.width ?? 280;
					const memberH = measured?.height ?? 70;
					const relX = member.position.x - groupX;
					const relY = member.position.y - groupY;
					maxRight = Math.max(maxRight, relX + memberW);
					maxBottom = Math.max(maxBottom, relY + memberH);
				}

				const newWidth = maxRight + 20; // GROUP_PADDING_HORIZONTAL
				const newHeight = maxBottom + 20; // GROUP_PADDING_BOTTOM

				if (
					Math.abs(newWidth - groupData.width) > 2 ||
					Math.abs(newHeight - groupData.height) > 2
				) {
					anyChanged = true;
					return {
						...node,
						data: {
							...groupData,
							width: newWidth,
							height: newHeight,
						},
					};
				}
				return node;
			});

			return anyChanged ? updatedNodes : currentNodes;
		});
	}, [setNodes]);

	// Execute a pending fitView intent. Called reactively from
	// handleNodesChangeSafe once ReactFlow has measured node dimensions.
	const executePendingFitView = useCallback(() => {
		const pending = pendingFitViewRef.current;
		if (!pending) {
			return;
		}
		pendingFitViewRef.current = null;

		if (pending.type === 'group') {
			const targetNodes = pending.graphDataNodes
				.filter((n) => {
					if (n.type === 'groupNode') {
						return n.id === `group-${pending.typename}`;
					}
					if (n.type === 'objectNode') {
						return (n.data as ObjectNodeData).typename === pending.typename;
					}
					return false;
				})
				.map((n) => ({ id: n.id }));
			fitView({ nodes: targetNodes, padding: pending.padding, duration: pending.duration });
		} else {
			fitView({ padding: pending.padding, duration: pending.duration });
		}
	}, [fitView]);

	// Filter out remove changes to prevent backspace/delete from removing nodes/edges.
	// Also serves as the reactive trigger for pending recalculations and fitView:
	// when ReactFlow reports dimension changes (meaning nodes have been measured),
	// we execute any pending operations that were recorded by the sync effects.
	const handleNodesChangeSafe = useCallback(
		(changes: any[]) => {
			const filtered = changes.filter((change: any) => change.type !== 'remove');

			// Track dimension changes from ReactFlow's measurement system.
			// Any node type (objectNode, groupNode, etc.) can trigger the
			// reactive recalc/fitView path. Previously only objectNode was
			// considered, which meant group collapses (where only the group
			// node's DOM size changes) never triggered fitView.
			let hasDimensionChange = false;
			for (const change of filtered) {
				if (change.type === 'dimensions' && change.dimensions && change.id) {
					nodeDimensionsRef.current.set(change.id, {
						width: change.dimensions.width,
						height: change.dimensions.height,
					});
					hasDimensionChange = true;
				}
			}

			onNodesChange(filtered);

			if (!hasDimensionChange) {
				return;
			}

			// Batch rapid dimension changes (ReactFlow may fire one per node)
			// using requestAnimationFrame so we process them once per frame.
			if (dimensionRafRef.current) {
				cancelAnimationFrame(dimensionRafRef.current);
			}
			dimensionRafRef.current = requestAnimationFrame(() => {
				dimensionRafRef.current = null;

				// 1. If a forced-layout recalculation is pending, execute it
				//    before fitView so group sizes are accurate.
				if (pendingRecalcRef.current) {
					pendingRecalcRef.current = false;
					recalculateGroupSizes();
				} else if (!isFocusMode) {
					// Normal dimension change (e.g. smart link card resolved) —
					// recalculate groups to accommodate the new measured size.
					recalculateGroupSizes();
				}

				// 2. Execute any pending fitView now that dimensions are settled
				//    and groups have been resized.
				executePendingFitView();
			});
		},
		[onNodesChange, recalculateGroupSizes, isFocusMode, executePendingFitView],
	);

	const handleEdgesChangeSafe = useCallback(
		(changes: any[]) => {
			const filtered = changes.filter((change: any) => change.type !== 'remove');
			onEdgesChange(filtered);
		},
		[onEdgesChange],
	);

	// ========================================================================
	// Determine current "screen" for rendering the appropriate ReactFlow.
	// Each screen uses a separate keyed ReactFlow instance to avoid
	// measurement/positioning issues between fundamentally different views.
	// ========================================================================
	const objectNodeCount = visibleNodes.filter((n) => n.type === 'objectNode').length;

	// ========================================================================
	// Error flags — derived reactively from atoms (Jotai-first).
	//
	// ErrorFlagsAtom computes the list of active error flags from:
	// - ObjectsQueryResultAtom (fetch-level and GraphQL-level query failures)
	// - DiscoveryFailuresAtom (per-direction relationship discovery failures)
	//
	// Dismissal is a transient UI concern: we track dismissed flag IDs in
	// local state and filter them out of the derived list for rendering.
	// ========================================================================
	const allErrorFlags = useAtomValue(ErrorFlagsAtom);
	const [dismissedFlagIds, setDismissedFlagIds] = useState<Set<string>>(new Set());

	// Reset dismissed flags when the underlying error flags change identity
	// (e.g. a new query produces a new error), so stale dismissals don't
	// suppress new errors with the same flag ID.
	const prevFlagKeysRef = useRef<string>('');
	const currentFlagKeys = allErrorFlags.map((f) => `${f.id}:${f.description}`).join('|');
	if (currentFlagKeys !== prevFlagKeysRef.current) {
		prevFlagKeysRef.current = currentFlagKeys;
		if (dismissedFlagIds.size > 0) {
			setDismissedFlagIds(new Set());
		}
	}

	const errorFlags = allErrorFlags.filter((f) => !dismissedFlagIds.has(f.id));

	const dismissFlag = useCallback((id: string | number) => {
		setDismissedFlagIds((prev) => new Set(prev).add(String(id)));
	}, []);

	// Shared non-interactive ReactFlow props
	const nonInteractiveProps = {
		nodeTypes,
		fitView: true as const,
		fitViewOptions: { padding: 0.2 },
		minZoom: 0.5,
		maxZoom: 1,
		zoomOnDoubleClick: false,
		deleteKeyCode: null as any,
		nodesConnectable: false,
		nodesDraggable: false,
		elementsSelectable: false,
		panOnDrag: false,
		zoomOnScroll: false,
		zoomOnPinch: false,
		preventScrolling: false,
		proOptions: REACT_FLOW_PRO_OPTIONS,
	};

	// Feedback button removed for the microsite embed; render nothing in its place.
	const feedbackButton: JSX.Element | null = null;

	// Error flags rendered outside ReactFlow
	const errorFlagGroup = (
		<FlagGroup onDismissed={dismissFlag}>
			{errorFlags.map((flag) => {
				const actions: Array<{ content: string; onClick: () => void }> = [];

				if (flag.traceIds && flag.traceIds.length > 0) {
					actions.push({
						content: flag.traceIds.length > 1 ? 'Copy traceIds' : 'Copy traceId',
						onClick: () => {
							navigator.clipboard.writeText(flag.traceIds!.join(','));
						},
					});
				}

				actions.push({
					content: 'Dismiss',
					onClick: () => dismissFlag(flag.id),
				});

				return (
					<Flag
						key={flag.id}
						icon={<ErrorIcon label="Error" />}
						actions={actions}
						id={flag.id}
						title={flag.title}
						description={flag.description}
						appearance={flag.appearance}
					/>
				);
			})}
		</FlagGroup>
	);

	// Determine whether we're still in the overall loading phase.
	// Stay in loading until:
	// 1. The initial cypher query has finished (isRunning === false)
	// 2. Collaborator discovery + hydration is complete (status === 'loaded' or 'error')
	const isCollaboratorsDone =
		collaboratorStatus === 'ready' ||
		collaboratorStatus === 'expanding' ||
		collaboratorStatus === 'error';
	const isFullyLoaded = !isRunning && isCollaboratorsDone;

	// Render loading state until all queries and follow-up resolutions complete
	if (!isFullyLoaded && !isFocusMode) {
		return (
			<React.Fragment>
				<ReactFlow
					key="loading"
					nodes={LOADING_NODES}
					edges={EMPTY_EDGES}
					css={styles.graphCanvas}
					{...nonInteractiveProps}
				>
					<Background
						variant={BackgroundVariant.Dots}
						gap={GRAPH_DOT_GAP}
						size={GRAPH_DOT_SIZE}
						color={GRAPH_DOT_COLOR}
					/>
					{feedbackButton}
				</ReactFlow>
				{errorFlagGroup}
			</React.Fragment>
		);
	}

	if (objectNodeCount === 0 && !isFocusMode) {
		if (queryResult !== undefined) {
			const noResultsNodes: Node<any>[] = [
				{
					id: 'no-results',
					type: 'noResultsHeaderNode',
					position: { x: 0, y: 0 },
					data: { _tag: 'noResultsHeader', onGoBack: undo },
					selectable: false,
					draggable: false,
					connectable: false,
				},
			];

			return (
				<React.Fragment>
					<ReactFlow
						key="no-results"
						nodes={noResultsNodes}
						edges={EMPTY_EDGES}
						css={styles.graphCanvas}
						{...nonInteractiveProps}
					>
						<Background
							variant={BackgroundVariant.Dots}
							gap={GRAPH_DOT_GAP}
							size={GRAPH_DOT_SIZE}
							color={GRAPH_DOT_COLOR}
						/>
						{feedbackButton}
					</ReactFlow>
					{errorFlagGroup}
				</React.Fragment>
			);
		}
	}

	// ========================================================================
	// Collaborators graph view — shows the user's collaborators and their
	// shared work items in a compact radial layout.
	// ========================================================================
	if (rootNodeId && !isFocusMode && collaboratorsGraph.nodes.length > 0) {
		return (
			<ExplorationContext.Provider value={explorationContextValue}>
				<CollaboratorsGraphView
					collaboratorsGraph={collaboratorsGraph}
					userNodeId={rootNodeId}
					feedbackButton={feedbackButton}
					errorFlagGroup={errorFlagGroup}
					fireEvent={fireEvent}
				/>
			</ExplorationContext.Provider>
		);
	}

	// Fallback: original masonry layout (for focus mode or when grouped graph isn't ready)
	return (
		<ExplorationContext.Provider value={explorationContextValue}>
			<ReactFlow
				nodes={nodes}
				edges={edges}
				onNodesChange={handleNodesChangeSafe}
				onEdgesChange={handleEdgesChangeSafe}
				onNodeClick={handleNodeClick}
				onNodeDoubleClick={handleNodeDoubleClick}
				onEdgeClick={handleEdgeClick}
				onPaneClick={handlePaneClick}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				fitView
				minZoom={0.1}
				maxZoom={2}
				zoomOnDoubleClick={false}
				deleteKeyCode={null}
				defaultEdgeOptions={{
					type: 'objectEdge',
					selectable: false,
				}}
				nodesConnectable={false}
				nodesDraggable={true}
				elementsSelectable={true}
				edgesFocusable={false}
				css={styles.graphCanvas}
				proOptions={REACT_FLOW_PRO_OPTIONS}
			>
				<Background
					variant={BackgroundVariant.Dots}
					gap={GRAPH_DOT_GAP}
					size={GRAPH_DOT_SIZE}
					color={GRAPH_DOT_COLOR}
				/>
				<Controls
					showInteractive={false}
					position="bottom-right"
				// onZoomIn={() => fireEvent('ui.button.clicked.gcZoomIn', {})}
				// onZoomOut={() => fireEvent('ui.button.clicked.gcZoomOut', {})}
				// onFitView={() => fireEvent('ui.button.clicked.gcFullScreen', {})}
				/>
				{feedbackButton}
			</ReactFlow>
			{errorFlagGroup}
		</ExplorationContext.Provider>
	);
}

// ============================================================================
// CollaboratorsGraphView — Compact radial layout for collaborator discovery.
// Separate component so that useForceLayout hooks are only called when active.
// ============================================================================

interface CollaboratorsGraphViewProps {
	collaboratorsGraph: ReturnType<typeof useCypherExplorationGraph>;
	userNodeId: string;
	feedbackButton: JSX.Element | null;
	errorFlagGroup: JSX.Element;
	fireEvent: ReturnType<typeof useAnalyticsEvents>['fireEvent'];
}

const EDGE_FLOW_KEYFRAMES = `
@keyframes edgeFlow {
  0% { stroke-dashoffset: 10; }
  100% { stroke-dashoffset: 0; }
}

/*
 * Promote each ReactFlow node wrapper to its own compositor layer.
 *
 * Without this, the wrapper repaints on every spring frame and the
 * browser's hit-test layer lags one frame behind the visible position
 * — so a newly-added node's bounding rect (used by elementFromPoint
 * and mouse-over events) doesn't match where the node is painted.
 *
 * Symptoms when missing:
 *   • Hovering a newly-added node during the spring does nothing
 *     (mouse hits the underlying .react-flow__pane instead).
 *   • Cursor stays as 'grab' (the pane's cursor) instead of switching
 *     to 'pointer' over the node card.
 *   • Edge-hover labels don't appear because the node never registers
 *     a hover.
 *
 * Once the spring settles, positions stop changing → the lag resolves
 * → hover/click work again. Promoting to a compositor layer keeps the
 * hit-test layer in sync with the visual position throughout the
 * spring.
 */
.react-flow__node {
  will-change: transform;
}
`;

function CollaboratorsGraphView({
	collaboratorsGraph,
	userNodeId,
	feedbackButton,
	errorFlagGroup,
	fireEvent,
}: CollaboratorsGraphViewProps): JSX.Element {
	const [graphNodes, setGraphNodes, onNodesChange] = useNodesState<Node<any>>(
		collaboratorsGraph.nodes,
	);
	const [graphEdges, setGraphEdges, onEdgesChange] = useEdgesState(collaboratorsGraph.edges);

	const { setNodes: rfSetNodes, setCenter, getZoom, fitView } = useReactFlow();
	const storeApi = useStoreApi();

	// DEBUG: expose ReactFlow's internal store + the controlled `graphNodes`
	// to `window` so agent-browser can inspect the truth on demand. Use
	// `window.__rfDebug.snapshot()` to grab a full snapshot.
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(window as any).__rfDebug = {
			storeApi,
			snapshot: (idSubstring?: string) => {
				const state = storeApi.getState();
				// `nodeLookup` holds the canonical node objects ReactFlow
				// uses for positioning, hit-testing, and rendering.
				const lookup = state.nodeLookup as Map<string, any>;
				const out: any = {
					storeNodeCount: lookup.size,
					controlledNodeCount: graphNodes.length,
					nodes: [],
				};
				for (const [id, n] of lookup.entries()) {
					if (idSubstring && !id.includes(idSubstring)) continue;
					const controlled = graphNodes.find((cn) => cn.id === id);
					out.nodes.push({
						id: id.slice(-50),
						position: n.position,
						internalsPosition: n.internals?.positionAbsolute,
						measured: n.measured,
						width: n.width,
						height: n.height,
						hidden: n.hidden,
						zIndex: n.zIndex,
						sameRefAsControlled: controlled === n,
						controlledPosition: controlled?.position,
						controlledMeasured: controlled?.measured,
					});
				}
				return out;
			},
			getNodes: () => storeApi.getState().nodes,
			getControlled: () => graphNodes,
		};
	}, [storeApi, graphNodes]);
	const prevGraphKeyRef = useRef<string>('');
	const prevDataKeyRef = useRef<string>('');
	const prevEdgeKeyRef = useRef<string>('');

	// Layout & recency atoms — drive magnetic pull-in + hide evaluation.
	const layoutNodes = useAtomValue(LayoutNodesAtom);
	const stampRecency = useSetAtom(StampRecencyAtom);
	const clearHidden = useSetAtom(ClearHiddenNodesAtom);

	// ── Selection + hover state ─────────────────────────────────────────
	//
	// `activeNodeId === null` is *default mode* (no selection). The user
	// node is no longer treated specially: it has no implicit selection
	// at landing, and clicking it follows the same selected-mode pipeline
	// as any other node. The user remains visually distinct (blue) only
	// because `centerNodeId === userNodeId` and `UserObjectNode` reads
	// that flag — selection logic itself is identity-agnostic.
	const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
	const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

	// ── Adjacency map ───────────────────────────────────────────────────
	const adjacencyMap = useMemo(() => {
		const map = new Map<string, Set<string>>();
		for (const edge of collaboratorsGraph.edges) {
			const src = edge.source as string;
			const tgt = edge.target as string;
			if (!map.has(src)) {
				map.set(src, new Set());
			}
			if (!map.has(tgt)) {
				map.set(tgt, new Set());
			}
			map.get(src)!.add(tgt);
			map.get(tgt)!.add(src);
		}
		return map;
	}, [collaboratorsGraph.edges]);

	// ── Smart fit helpers ────────────────────────────────────────────────
	//
	// In selected mode, a clicked node's 1-hop neighbours don't sit at
	// their radial-layout rest positions — they are pulled onto a
	// magnetic ring around the selection by `computePullInTargets`.
	// To ensure fitView aims the viewport at the **final post-spring**
	// positions (not transient mid-flight or stale rest positions), we
	// recompute the ring slot positions here from the same inputs that
	// `applySelectionTargets` uses, then derive the bounding box from
	// those.
	//
	// The fitView call itself is fired immediately so the viewport
	// pan/zoom animation runs in parallel with the magnetic spring —
	// the user actually sees the magnetic spring play out inside the
	// new viewport instead of staring at lines moving around after.

	/**
	 * Compute the **final post-spring** axis-aligned bounding box (in
	 * graph-space, top-left + width/height) of the selected node and its
	 * 1-hop ring, using the same `computePullInTargets` math that the
	 * magnetic system applies. Returns `null` if the selected node has
	 * no rest position (cannot anchor the ring).
	 *
	 * Each ring slot's CENTER is computed as `selectedCenter + ring
	 * offset` and we expand to the slot's bounding box using the
	 * neighbour's measured/estimated size. This matches exactly where
	 * each node will sit once its spring settles.
	 */
	const computeSelectionFinalBBox = useCallback(
		(selectedId: string): { x: number; y: number; width: number; height: number } | null => {
			const selectedRestNode = layoutNodes.get(selectedId);
			if (!selectedRestNode) {
				return null;
			}
			const selectedSize = estimateLayoutSize(selectedRestNode);
			const selectedCenter = {
				x: selectedRestNode.x + selectedSize.width / 2,
				y: selectedRestNode.y + selectedSize.height / 2,
			};

			// Build the same restPositions + sizes maps that
			// `applySelectionTargets` builds, restricted to layout
			// entries we actually have. Anything missing is just
			// excluded from the slot calculation (and the bbox).
			const restPositions = new Map<string, { x: number; y: number }>();
			const sizes = new Map<string, { width: number; height: number }>();
			for (const [id, ln] of layoutNodes) {
				restPositions.set(id, { x: ln.x, y: ln.y });
				sizes.set(id, estimateLayoutSize(ln));
			}

			const neighbourIds = Array.from(adjacencyMap.get(selectedId) ?? []).filter((id) =>
				layoutNodes.has(id),
			);

			const slots = computePullInTargets({
				selectedId,
				selectedRest: selectedCenter,
				selectedSize,
				neighbourIds,
				restPositions,
				focusNodeId: userNodeId,
				sizes,
			});

			// Start the bbox at the selected node's own rect.
			let minX = selectedRestNode.x;
			let minY = selectedRestNode.y;
			let maxX = selectedRestNode.x + selectedSize.width;
			let maxY = selectedRestNode.y + selectedSize.height;

			// Each slot is a displacement-from-rest. The final TOP-LEFT
			// of a neighbour at its slot is `restTopLeft + (target - 0)`
			// — i.e. the slot's `targetX/Y` already encode the offset
			// needed to move the neighbour's CENTER to the ring point,
			// expressed relative to its rest position. So the final
			// top-left is `restPos + (target)` (target is a delta).
			for (const slot of slots) {
				const restPos = restPositions.get(slot.id);
				const size = sizes.get(slot.id);
				if (!restPos || !size) {
					continue;
				}
				const finalTopLeftX = restPos.x + slot.targetX;
				const finalTopLeftY = restPos.y + slot.targetY;
				minX = Math.min(minX, finalTopLeftX);
				minY = Math.min(minY, finalTopLeftY);
				maxX = Math.max(maxX, finalTopLeftX + size.width);
				maxY = Math.max(maxY, finalTopLeftY + size.height);
			}

			return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
		},
		[adjacencyMap, layoutNodes, userNodeId],
	);

	/**
	 * Returns true if `bbox` (in graph-space) would fit inside the
	 * current ReactFlow viewport **size** at the current zoom, with
	 * `paddingPx` breathing room. This is a SIZE-ONLY check: it does
	 * NOT consider where the bbox currently sits on screen, because
	 * `handleNodeClick` always fires `setCenter(...)` to pan the
	 * selected node to the viewport center BEFORE this check runs.
	 * After that pan, the bbox's center will coincide with the
	 * viewport center — so the only remaining question is whether
	 * the bbox is small enough to fit.
	 *
	 * Using a position-aware check here would cause spurious refits
	 * on every selection (because pre-pan, the bbox's position is
	 * still wherever the previous selection left the viewport — not
	 * where it will be a moment later when setCenter completes).
	 */
	const bboxFitsInViewport = useCallback(
		(
			bbox: { x: number; y: number; width: number; height: number },
			paddingPx: number = 32,
		): boolean => {
			const state = storeApi.getState() as {
				width: number;
				height: number;
			};
			const zoom = getZoom();
			const availableW = Math.max(0, state.width - paddingPx * 2);
			const availableH = Math.max(0, state.height - paddingPx * 2);
			return bbox.width * zoom <= availableW && bbox.height * zoom <= availableH;
		},
		[storeApi, getZoom],
	);

	/**
	 * Build the fitView target id list for a given selected node:
	 * the node itself plus its 1-hop neighbours that have a layout
	 * entry. Used to tell ReactFlow which nodes to frame.
	 */
	const buildSelectionTargetIds = useCallback(
		(selectedId: string): string[] => {
			const ids: string[] = [];
			if (layoutNodes.has(selectedId)) {
				ids.push(selectedId);
			}
			const neighbours = adjacencyMap.get(selectedId);
			if (neighbours) {
				for (const n of neighbours) {
					if (layoutNodes.has(n)) {
						ids.push(n);
					}
				}
			}
			return ids;
		},
		[adjacencyMap, layoutNodes],
	);

	/**
	 * If the selected node's 1-hop ring (using **final post-spring**
	 * ring slot positions) doesn't fit in the current viewport, kick
	 * off a fitView animation aimed at the ring's bounding box. The
	 * fitView animation runs in parallel with the magnetic spring so
	 * the user sees the spring motion settle inside the new viewport.
	 */
	const fitViewToSelectionIfNeeded = useCallback(
		(selectedId: string) => {
			const bbox = computeSelectionFinalBBox(selectedId);
			if (!bbox) {
				return;
			}
			if (bboxFitsInViewport(bbox)) {
				return;
			}
			const targetIds = buildSelectionTargetIds(selectedId);
			if (targetIds.length === 0) {
				return;
			}
			fitView({
				nodes: targetIds.map((id) => ({ id })),
				padding: 0.2,
				duration: 400,
			});
		},
		[computeSelectionFinalBBox, bboxFitsInViewport, buildSelectionTargetIds, fitView],
	);

	// ── Expanded node IDs (hover or selected, excluding user nodes) ─────
	const expandedNodeIds = useMemo(() => {
		const set = new Set<string>();
		const userTypes = new Set(['userObjectNode']);
		const nodeMap = new Map(collaboratorsGraph.nodes.map((n) => [n.id, n]));

		const addIfNonUser = (nodeId: string | null) => {
			if (!nodeId) {
				return;
			}
			const node = nodeMap.get(nodeId);
			if (node && !userTypes.has(node.type || '')) {
				set.add(nodeId);
			}
		};

		addIfNonUser(hoveredNodeId);
		// In selected mode (`activeNodeId !== null`) the active node is
		// the focal point of expansion; expose it to consumers.
		if (activeNodeId) {
			addIfNonUser(activeNodeId);
		}
		return set;
	}, [hoveredNodeId, activeNodeId, collaboratorsGraph.nodes]);

	// ── Selection context value ─────────────────────────────────────────
	// Note: animatedEdgePairs is always empty — the default-mode mouse-following
	// path-finding animation has been removed. Selection-mode visualisation now
	// relies on selection-based opacity + hover-revealed verb labels on edges.
	const animatedEdgePairs = useMemo(() => new Set<string>(), []);

	// In default mode (`activeNodeId === null`) we collapse the active id
	// onto `centerNodeId` so consumers can keep reading a non-null active
	// id. `hasActiveSelection` preserves the distinction between landing
	// default mode and explicitly selecting the user node.
	const effectiveActiveNodeId = activeNodeId ?? userNodeId;
	const hasActiveSelection = activeNodeId !== null;

	const selectionContext = useMemo<GraphSelectionState>(
		() => ({
			activeNodeId: effectiveActiveNodeId,
			hoveredNodeId,
			centerNodeId: userNodeId,
			hasActiveSelection,
			adjacency: adjacencyMap,
			expandedNodeIds,
			animatedEdgePairs,
		}),
		[
			effectiveActiveNodeId,
			hoveredNodeId,
			userNodeId,
			hasActiveSelection,
			adjacencyMap,
			expandedNodeIds,
			animatedEdgePairs,
		],
	);

	// Z-index sync. We MUST NOT replace node object references here:
	// `selectionContext` re-evaluates on every hover (`hoveredNodeId`
	// is part of it), and spreading a new object per node (`{ ...node,
	// zIndex }`) causes ReactFlow to remount affected nodes — which
	// reproduces the very same identity-churn class of bug we already
	// fixed in `useCypherExplorationGraph`/this component's main sync
	// effect (cursor flickering between point/grab, hover label flicker,
	// edges appearing/disappearing). Mutate `zIndex` in place on the
	// existing object instead. ReactFlow's `setNodes` doesn't
	// shallow-compare nodes — it just re-renders the wrapper layers
	// affected by the changed `zIndex`. This matches the in-place
	// mutation pattern the magnetic rAF loop already uses for
	// `position`.
	useEffect(() => {
		rfSetNodes((currentNodes) => {
			let changed = false;
			for (const node of currentNodes) {
				const zIndex = computeNodeZIndex(node.id, selectionContext);
				if ((node.zIndex ?? 0) === zIndex) {
					continue;
				}
				// eslint-disable-next-line no-param-reassign
				node.zIndex = zIndex;
				changed = true;
			}
			// Return a new top-level array reference only when something
			// actually changed, so ReactFlow knows to re-emit. The inner
			// node references stay identical → no remount.
			return changed ? [...currentNodes] : currentNodes;
		});
	}, [rfSetNodes, selectionContext]);

	// ── Radial-magnetic layout ─
	const {
		start,
		drag,
		stop,
		reheat,
		pin,
		unpin,
		setMagneticTargets,
		clearMagneticTargets,
		setSelectionOrigin,
	} = useRadialMagneticLayout();

	// ── Cypher expansion on click ────────────────────────────────────────
	const expandNode = useSetAtom(ExpandNodeAtom);
	const expandedNodeIds_layout = useAtomValue(ExpandedNodeIdsAtom);
	const expandingNodeIds_layout = useAtomValue(ExpandingNodeIdsAtom);

	const applySelectionTargets = useCallback(
		(selectedId: string, fallbackPosition?: { x: number; y: number }) => {
			const neighbourIds = Array.from(adjacencyMap.get(selectedId) ?? []);
			const restPositions = new Map<string, { x: number; y: number }>();
			const sizes = new Map<string, { width: number; height: number }>();
			for (const [id, ln] of layoutNodes) {
				restPositions.set(id, { x: ln.x, y: ln.y });
				sizes.set(id, estimateLayoutSize(ln));
			}

			// `ln.x / ln.y` are the node's **top-left** corner in graph space
			// (as written by RadialLayoutEngine). The pull-in ring must be
			// centered at the node's **visual center** so that ring slots are
			// placed equidistant from its edges — not from its corner, which
			// would cause nodes at right/bottom angles to land almost on top
			// of the selected node's edge while nodes at left/top angles land
			// comfortably far away.
			const topLeft = restPositions.get(selectedId) ?? fallbackPosition;
			if (!topLeft) {
				return;
			}
			const selectedSize = sizes.get(selectedId);
			const selectedCenter = {
				x: topLeft.x + (selectedSize?.width ?? 0) / 2,
				y: topLeft.y + (selectedSize?.height ?? 0) / 2,
			};

			const slots = computePullInTargets({
				selectedId,
				selectedRest: selectedCenter,
				selectedSize,
				neighbourIds,
				restPositions,
				focusNodeId: userNodeId,
				sizes,
			});
			setMagneticTargets(slots);
		},
		[adjacencyMap, layoutNodes, setMagneticTargets, userNodeId],
	);

	// Whenever the layout updates while a node selection is active,
	// recompute the *whole* pull-in ring from scratch. This covers two
	// cases that both need to look identical to the user:
	//
	//   1. The clicked node's expansion just resolved, adding fresh
	//      neighbours to `LayoutNodesAtom`. The hook has already seeded
	//      those new nodes from the selection origin (atomic, no flash);
	//      we now need to assign them slot targets so they spring out
	//      to the ring.
	//   2. Existing siblings that were already on the ring need to be
	//      pulled/pushed to their new slot — the slot count just grew,
	//      so the per-slot angles change.
	//
	// Re-running `applySelectionTargets` does both: it re-derives every
	// slot from `adjacencyMap` + `layoutNodes`, so existing nodes get
	// new targets and new nodes get their first target.
	useLayoutEffect(() => {
		// Default mode — nothing to spring around.
		if (activeNodeId === null) {
			return;
		}
		const activeLayoutNode = layoutNodes.get(activeNodeId);
		if (!activeLayoutNode) {
			return;
		}

		// Guard: wait until ALL neighbours known to `adjacencyMap` have
		// rest positions in `layoutNodes` before computing ring slots.
		//
		// Without this, the effect fires twice per expansion:
		//   Pass 1 — `layoutNodes` updated (new nodes in rest map) but
		//             `adjacencyMap` still stale → partial ring with old
		//             neighbour count → wrong slot angles for everyone.
		//   Pass 2 — `adjacencyMap` updated → full ring → every node's
		//             slot angle changes → jarring direction-reversal.
		//
		// Once the guard passes, both sources are in sync and we call
		// `applySelectionTargets` exactly once with the complete set.
		const neighbourIds = Array.from(adjacencyMap.get(activeNodeId) ?? []);
		const allNeighboursInLayout = neighbourIds.every((id) => layoutNodes.has(id));
		if (!allNeighboursInLayout) {
			return;
		}

		const activeSize = estimateLayoutSize(activeLayoutNode);
		applySelectionTargets(activeNodeId, {
			x: activeLayoutNode.x + activeSize.width / 2,
			y: activeLayoutNode.y + activeSize.height / 2,
		});
	}, [activeNodeId, adjacencyMap, applySelectionTargets, layoutNodes]);

	// ── Refit when new neighbours arrive during selected mode ──────────
	//
	// When a node is selected and the API returns fresh neighbours (their
	// rest positions land in `LayoutNodesAtom`), the previously-computed
	// viewport may no longer be able to accommodate the expanded ring.
	// In that case fire a follow-up fitView aimed at the new rest-position
	// bounding box. Only runs in selected mode — per product requirement,
	// we ignore arrivals when the user is in default mode.
	const prevLayoutNodeIdsRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		// Snapshot the current set of layout-node ids so we can diff next
		// pass regardless of whether we act on this one.
		const currentIds = new Set(layoutNodes.keys());

		// Default mode: just record current ids and skip — per
		// requirement, ignore arrivals when not in selected mode.
		if (activeNodeId === null) {
			prevLayoutNodeIdsRef.current = currentIds;
			return;
		}

		// Detect whether at least one new id has appeared in `layoutNodes`
		// since the previous evaluation (i.e. fresh rest positions just
		// landed from an API expansion).
		let hasNewIds = false;
		for (const id of currentIds) {
			if (!prevLayoutNodeIdsRef.current.has(id)) {
				hasNewIds = true;
				break;
			}
		}
		prevLayoutNodeIdsRef.current = currentIds;

		if (!hasNewIds) {
			return;
		}

		// New rest positions just arrived → check whether the selected
		// node's 1-hop ring still fits, and refit if not. Animation
		// fires immediately so it runs in parallel with the spring.
		fitViewToSelectionIfNeeded(activeNodeId);
	}, [layoutNodes, activeNodeId, fitViewToSelectionIfNeeded]);

	// ── Event handlers ──────────────────────────────────────────────────
	const handleNodeClick = useCallback(
		(_event: React.MouseEvent, node: Node) => {
			recordGraphExplorerTestModeNodeClick(node);
			// The user node is treated like any other node: clicking it
			// selects it (and triggers discovery/expansion if not yet
			// expanded), clicking it again returns to default mode.
			//
			// Toggle: if the clicked node is already active, deselect
			// (default mode). Otherwise, select it.
			const currentZoom = getZoom();
			const isDeselect = activeNodeId === node.id;
			const nextActiveNodeId: string | null = isDeselect ? null : node.id;
			setActiveNodeId(nextActiveNodeId);

			if (nextActiveNodeId !== null) {
				// Centre on the selected node's **resting** position, not
				// its live `node.position` — `node.position` may be a
				// transient mid-spring location (e.g. when re-clicking a
				// node that was just pulled into the previous selection's
				// ring), and panning there would leave the viewport
				// off-target after the spring releases the node back to
				// its rest position.
				//
				// Once selected, a node's final on-screen location is
				// always its rest position (the ring centres on the
				// selected node — it has zero displacement applied to
				// itself). Falling back to `node.position` only when the
				// rest position isn't yet known.
				const restNode = layoutNodes.get(node.id);
				const restSize = restNode ? estimateLayoutSize(restNode) : null;
				const nodeW = restSize?.width ?? node.measured?.width ?? 150;
				const nodeH = restSize?.height ?? node.measured?.height ?? 30;
				const restX = restNode?.x ?? node.position.x;
				const restY = restNode?.y ?? node.position.y;
				setCenter(restX + nodeW / 2, restY + nodeH / 2, {
					duration: 400,
					zoom: currentZoom,
				});

				// Stamp recency so the newly-focused node + its existing
				// neighbours win the next overlap evaluation.
				const neighbourIds = Array.from(adjacencyMap.get(node.id) ?? []);
				stampRecency([node.id, ...neighbourIds]);

				// Declare the spawn-from origin *before* triggering the
				// async expansion. The hook's `LayoutNodesAtom` sync effect
				// reads `selectionOriginRef` and seeds any freshly-added
				// nodes from this origin in the same tick they appear —
				// guaranteeing they paint at the clicked node's center on
				// their very first frame (no flash at rest position).
				// Use the node's visual center as the spawn origin so new children
				// appear at the center of the clicked node (not its top-left corner).
				const layoutNodeForClicked = layoutNodes.get(node.id);
				const clickedSize = layoutNodeForClicked ? estimateLayoutSize(layoutNodeForClicked) : null;
				const origin = layoutNodeForClicked
					? {
						x: layoutNodeForClicked.x + (clickedSize?.width ?? 0) / 2,
						y: layoutNodeForClicked.y + (clickedSize?.height ?? 0) / 2,
					}
					: { x: node.position.x, y: node.position.y };
				setSelectionOrigin(origin);

				const willExpand =
					!expandedNodeIds_layout.has(node.id) && !expandingNodeIds_layout.has(node.id);

				// Magnetic pull-in ring: only compute immediately if the
				// node is already fully expanded (all neighbours already in
				// the layout). When expansion is about to fire, we *skip*
				// the eager ring call here and let the `useLayoutEffect`
				// watching `layoutNodes` do the single, definitive ring
				// computation once the full neighbour set has arrived.
				//
				// Calling it twice (before + after expansion) would assign
				// different slot angles to existing neighbours — causing a
				// jarring "changes direction mid-spring" visual artefact
				// because the slot count changes between the two calls.
				if (!willExpand) {
					applySelectionTargets(node.id, origin);
				}

				// Trigger cypher expansion for any not-yet-expanded node
				// (including the user). New arrivals will trigger an
				// overlap evaluation in the expansion atom.
				if (willExpand) {
					expandNode(node.id);
				}

				// If the selected node's already-known 1-hop ring (using
				// resting positions) doesn't fit in the current viewport,
				// kick off a fitView animation immediately. We do this in
				// parallel with the spring so the user sees the magnetic
				// motion play out *inside* the new viewport. For nodes
				// whose neighbours are still loading, the new-arrivals
				// useEffect below will fire a follow-up fitView once
				// fresh rest positions land in `LayoutNodesAtom`.
				fitViewToSelectionIfNeeded(node.id);
			} else {
				// Returning to default mode: pan back to the user node so
				// the constellation reads from a stable, familiar centre.
				const userLayoutNode = layoutNodes.get(userNodeId);
				const userSize = userLayoutNode
					? estimateLayoutSize(userLayoutNode)
					: { width: 0, height: 0 };
				setCenter(
					(userLayoutNode?.x ?? 0) + userSize.width / 2,
					(userLayoutNode?.y ?? 0) + userSize.height / 2,
					{ duration: 400, zoom: currentZoom },
				);
				// No active selection → drop the spawn origin and release
				// any active spring targets so everything springs to rest.
				setSelectionOrigin(null);
				clearMagneticTargets();
			}
		},
		[
			activeNodeId,
			userNodeId,
			setCenter,
			getZoom,
			expandNode,
			expandedNodeIds_layout,
			expandingNodeIds_layout,
			adjacencyMap,
			layoutNodes,
			stampRecency,
			applySelectionTargets,
			clearMagneticTargets,
			setSelectionOrigin,
			fitViewToSelectionIfNeeded,
		],
	);

	const handlePaneClick = useCallback(() => {
		// Pane click → Discovery mode (no active selection). The user
		// node stays blue (driven by `centerNodeId`), but no node is
		// "selected" so background opacity / pull-in rings are released.
		//
		// We deliberately do NOT mutate any atom rest positions here.
		// Every node's `LayoutNodesAtom` entry is its permanent rest
		// position assigned by `expandLayout` at the time the node was
		// first added. Releasing the magnetic targets lets the spring
		// carry every node back to that exact rest position — so
		// repeated select/deselect cycles produce identical visuals
		// with zero drift.
		//
		// Overlap avoidance is handled at expansion time inside
		// `placeAroundCenter` (per-node radial fallback), so there's
		// nothing to "fix up" here.
		setActiveNodeId(null);
		setSelectionOrigin(null);
		clearMagneticTargets();
		clearHidden();
	}, [clearMagneticTargets, clearHidden, setSelectionOrigin]);

	const hoveredNodeIdRef = useRef<string | null>(null);

	/**
	 * Wrap the magnetic-layout `stop` handler so we ALSO clear ReactFlow's
	 * internal selection of the dragged node. Without this, ReactFlow
	 * keeps the dragged node `selected: true` after release; the next
	 * drag of a different node then drags BOTH nodes (because the
	 * still-selected one is part of the multi-selection that ReactFlow
	 * moves together). We force `selected: false` for the dragged node
	 * (and any other node that may be lingering as selected) so each
	 * drag gesture is independent.
	 */
	const handleNodeDragStop = useCallback(
		(event: React.MouseEvent, node: Node, _nodes: Node[]) => {
			stop(event, node);
			// Force-deselect the dragged node AND any node that may
			// still be lingering as selected, so the very next drag
			// gesture starts from a clean selection state. Without
			// this, ReactFlow's multi-selection logic causes the
			// previously-dragged node to come along for the ride
			// when the user starts a new drag elsewhere.
			const deselectChanges = [
				{ id: node.id, type: 'select' as const, selected: false },
				...graphNodes
					.filter((n) => n.selected && n.id !== node.id)
					.map((n) => ({ id: n.id, type: 'select' as const, selected: false })),
			];
			onNodesChange(deselectChanges);
		},
		[stop, onNodesChange, graphNodes],
	);

	const handleNodeMouseEnter = useCallback(
		(_event: React.MouseEvent, node: Node) => {
			if (hoveredNodeIdRef.current && hoveredNodeIdRef.current !== node.id) {
				unpin(hoveredNodeIdRef.current);
			}
			hoveredNodeIdRef.current = node.id;
			setHoveredNodeId(node.id);
			pin(node.id);
		},
		[pin, unpin],
	);

	const handleNodeMouseLeave = useCallback(() => {
		if (hoveredNodeIdRef.current) {
			unpin(hoveredNodeIdRef.current);
			hoveredNodeIdRef.current = null;
		}
		setHoveredNodeId(null);
	}, [unpin]);

	// ── Sync graph data into ReactFlow ──────────────────────────────────
	//
	// Important: do NOT replace the entire ReactFlow nodes/edges array
	// when new nodes arrive. Doing so substitutes brand-new object
	// references for *every* surviving node, which causes ReactFlow to
	// remount nodes mid-spring — manifesting as cursor flicker between
	// `pointer`/`grab`, hover-label flicker, and edges appearing /
	// disappearing while the magnetic animation is still running.
	//
	// Instead we incrementally merge:
	//   • surviving ids: keep the *current* ReactFlow node object
	//     (preserves measured/dragging/selected/zIndex set by ReactFlow);
	//     only swap `data` / `type` if they actually changed.
	//   • new ids:        append the freshly-built node object.
	//   • orphan ids:     drop them.
	// Position is owned by the magnetic rAF loop in
	// useRadialMagneticLayout; we never overwrite it from here.
	//
	// `useLayoutEffect` so the very first commit (with non-root nodes
	// collapsed onto the user origin for the landing spring-out) lands
	// before the browser paints — preventing a one-frame "rest flash"
	// before the magnetic loop pulls the children back to origin.
	useLayoutEffect(() => {
		if (collaboratorsGraph.nodes.length === 0) {
			return;
		}

		const graphKey = collaboratorsGraph.nodes
			.map((n) => n.id)
			.sort()
			.join(',');
		const isStructureChange = graphKey !== prevGraphKeyRef.current;

		const dataKey = collaboratorsGraph.nodes
			.map((n) => `${n.id}:${(n.data as any)?.displayName ?? ''}`)
			.join(',');
		const isDataChange = dataKey !== prevDataKeyRef.current;

		// Track edge changes separately — cross-edges can appear between
		// two *already-visible* nodes (no node structure change), so we
		// must push updated edges to ReactFlow whenever the edge set grows,
		// not only when nodes change.
		const edgeKey = collaboratorsGraph.edges
			.map((e) => e.id)
			.sort()
			.join(',');
		const isEdgeChange = edgeKey !== prevEdgeKeyRef.current;

		if (!isStructureChange && !isDataChange && !isEdgeChange) {
			return;
		}

		const isFirstLoad = prevGraphKeyRef.current === '';
		prevGraphKeyRef.current = graphKey;
		prevDataKeyRef.current = dataKey;
		prevEdgeKeyRef.current = edgeKey;

		if (isFirstLoad) {
			// First commit. To make the landing animation match a
			// click-to-select expansion (children spring *out* from the
			// user node) we collapse the first paint of every non-root
			// node onto the root node's visual centre. The magnetic
			// hook's `[layoutNodes]` effect simultaneously seeds each
			// new node's spring `disp` so `base + disp ≈ rest origin`
			// for its first visible frame, and the spring then decays
			// disp toward zero — i.e. the children spring out to the
			// ring naturally.
			//
			// Without this collapse the first frame would paint the
			// children at their rest positions and the magnetic loop
			// would only kick in on a subsequent rAF tick — producing
			// a one-frame "pop" before the spring takes over.
			let rootRest: { x: number; y: number } | null = null;
			let rootSize = { width: 0, height: 0 };
			for (const node of collaboratorsGraph.nodes) {
				const ln = layoutNodes.get(node.id);
				if (ln && !ln.parentId) {
					rootRest = { x: ln.x, y: ln.y };
					rootSize = estimateLayoutSize(ln);
					break;
				}
			}
			if (rootRest) {
				const originX = rootRest.x + rootSize.width / 2;
				const originY = rootRest.y + rootSize.height / 2;
				const collapsed = collaboratorsGraph.nodes.map((node) => {
					const ln = layoutNodes.get(node.id);
					if (!ln || !ln.parentId) {
						// Root node: keep its actual rest position.
						return node;
					}
					return {
						...node,
						position: { x: originX, y: originY },
					};
				});
				setGraphNodes(collapsed);
			} else {
				setGraphNodes(collaboratorsGraph.nodes);
			}
			setGraphEdges(collaboratorsGraph.edges);
			return;
		}

		if (isStructureChange || isDataChange) {
			const incomingById = new Map(collaboratorsGraph.nodes.map((n) => [n.id, n]));

			// Use `setGraphNodes` (from `useNodesState`) rather than
			// `rfSetNodes` (from `useReactFlow`). The d3-force reference
			// implementation does the same: graphNodes is the source of
			// truth, and ReactFlow's internal store is rebuilt from
			// graphNodes whenever it changes. ResizeObserver dimension
			// changes flow back into graphNodes via `onNodesChange` →
			// `applyNodeChanges`, which preserves `measured` across
			// subsequent renders.
			//
			// Going through `rfSetNodes` here was the source of the
			// "newly-added nodes lose measured during the spring"
			// regression: rfSetNodes wrote directly to the store but
			// the dimension change couldn't propagate back to graphNodes
			// for newly-added ids fast enough — every subsequent prop
			// sync from graphNodes wiped the just-arrived measurement.
			setGraphNodes((currentNodes) => {
				const seen = new Set<string>();
				const merged: Node<any>[] = [];
				let mutated = false;

				for (const current of currentNodes) {
					const incoming = incomingById.get(current.id);
					if (!incoming) {
						// Orphaned node — drop it.
						mutated = true;
						continue;
					}
					seen.add(current.id);

					if (incoming === current) {
						// Identity-stable from the hook — nothing to do.
						merged.push(current);
						continue;
					}

					const sameType = current.type === incoming.type;
					const sameData = current.data === incoming.data;
					if (sameType && sameData) {
						merged.push(current);
						continue;
					}

					// `current` and `incoming` differ on `type` and/or
					// `data`. Mutate `current` *in place* rather than
					// spreading into a new object — spreading would
					// give ReactFlow a fresh node reference, which
					// remounts the node wrapper mid-animation (the
					// "newly-added node remounts during the spring"
					// regression). We mutate `current` (the object
					// already in ReactFlow's store) so the store keeps
					// the same reference but picks up the new data on
					// the next render. Mirrors the in-place `zIndex`
					// sync pattern above. We must still return a new
					// outer array so ReactFlow's `setNodes` re-emits.
					if (!sameType) {
						// eslint-disable-next-line no-param-reassign
						(current as { type: string }).type = incoming.type ?? 'default';
					}
					if (!sameData) {
						// eslint-disable-next-line no-param-reassign
						current.data = incoming.data;
					}
					mutated = true;
					merged.push(current);
				}

				// Append genuinely new nodes (preserve their freshly-built
				// objects so first paint sees correct position/data).
				for (const incoming of collaboratorsGraph.nodes) {
					if (seen.has(incoming.id)) continue;
					mutated = true;
					merged.push(incoming);
				}

				return mutated ? merged : currentNodes;
			});
		}

		if (isStructureChange || isEdgeChange) {
			const incomingById = new Map(collaboratorsGraph.edges.map((e) => [e.id, e]));

			setGraphEdges((currentEdges) => {
				const seen = new Set<string>();
				const merged: Edge<any>[] = [];
				let mutated = false;

				for (const current of currentEdges) {
					const incoming = incomingById.get(current.id);
					if (!incoming) {
						mutated = true;
						continue;
					}
					seen.add(current.id);

					if (incoming === current) {
						merged.push(current);
						continue;
					}

					const sameSource = current.source === incoming.source;
					const sameTarget = current.target === incoming.target;
					const sameData = current.data === incoming.data;
					const sameType = current.type === incoming.type;
					if (sameSource && sameTarget && sameData && sameType) {
						merged.push(current);
						continue;
					}

					mutated = true;
					merged.push({
						...current,
						type: sameType ? current.type : incoming.type,
						source: sameSource ? current.source : incoming.source,
						target: sameTarget ? current.target : incoming.target,
						data: sameData ? current.data : incoming.data,
					});
				}

				for (const incoming of collaboratorsGraph.edges) {
					if (seen.has(incoming.id)) continue;
					mutated = true;
					merged.push(incoming);
				}

				return mutated ? merged : currentEdges;
			});
		}
	}, [
		collaboratorsGraph.nodes,
		collaboratorsGraph.edges,
		setGraphNodes,
		setGraphEdges,
		layoutNodes,
	]);

	// Force-layout reheat is a no-op under the magnetic system.
	useEffect(() => {
		reheat(0.15);
	}, [expandedNodeIds, reheat]);

	// ── Click-to-activate scroll-zoom ──────────────────────────────────
	// React Flow has no built-in "only zoom when active/focused" flag, so
	// we track it ourselves. While inactive, wheel events bubble to the
	// page (`preventScrolling={false}` + `zoomOnScroll={false}`) so the
	// surrounding microsite scrolls normally instead of being hijacked.
	// The canvas activates on mousedown inside it, and deactivates on
	// mousedown outside or when it scrolls out of the viewport.
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const [isCanvasActive, setIsCanvasActive] = useState(false);

	useEffect(() => {
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const unbind = bind(doc, {
			type: 'mousedown',
			listener: (event: MouseEvent) => {
				const el = viewportRef.current;
				if (!el) {
					return;
				}
				const target = event.target as globalThis.Node | null;
				if (target && el.contains(target)) {
					setIsCanvasActive(true);
				} else {
					setIsCanvasActive(false);
				}
			},
			options: { capture: true },
		});
		return () => {
			unbind();
		};
	}, []);

	useEffect(() => {
		const el = viewportRef.current;
		if (!el || typeof IntersectionObserver === 'undefined') {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) {
						setIsCanvasActive(false);
					}
				}
			},
			{ threshold: 0.1 },
		);
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<GraphSelectionContext.Provider value={selectionContext}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles */}
			<style dangerouslySetInnerHTML={{ __html: EDGE_FLOW_KEYFRAMES }} />
			<div ref={viewportRef} css={styles.graphViewport}>
				<ReactFlow
					nodes={graphNodes}
					edges={graphEdges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}
					onNodeClick={handleNodeClick}
					onPaneClick={handlePaneClick}
					onNodeMouseEnter={handleNodeMouseEnter}
					onNodeMouseLeave={handleNodeMouseLeave}
					nodeTypes={nodeTypes}
					edgeTypes={edgeTypes}
					onNodeDragStart={start}
					onNodeDrag={drag}
					onNodeDragStop={handleNodeDragStop}
					minZoom={0.1}
					maxZoom={4}
					zoomOnDoubleClick={false}
					zoomOnScroll={isCanvasActive}
					zoomOnPinch={isCanvasActive}
					panOnScroll={false}
					preventScrolling={isCanvasActive}
					deleteKeyCode={null}
					defaultEdgeOptions={{ selectable: false }}
					nodesConnectable={false}
					nodesDraggable={true}
					elementsSelectable={true}
					edgesFocusable={false}
					selectNodesOnDrag={false}
					css={styles.graphCanvas}
					proOptions={REACT_FLOW_PRO_OPTIONS}
				>
					<Background
						variant={BackgroundVariant.Dots}
						gap={GRAPH_DOT_GAP}
						size={GRAPH_DOT_SIZE}
						color={GRAPH_DOT_COLOR}
					/>
					<Controls
						showInteractive={false}
						position="bottom-right"
					// onZoomIn={() => fireEvent('ui.button.clicked.gcZoomIn', {})}
					// onZoomOut={() => fireEvent('ui.button.clicked.gcZoomOut', {})}
					// onFitView={() => fireEvent('ui.button.clicked.gcFullScreen', {})}
					/>
					{feedbackButton}
				</ReactFlow>
			</div>
			{errorFlagGroup}
		</GraphSelectionContext.Provider>
	);
}

interface ObjectsVisualizerProps {
	onExplorationStart: () => void;
	/** Whether the left (explorer) floating panel is currently visible. */
	showLeftPanel?: boolean;
	/** Width of the left floating panel in pixels. */
	leftPanelWidth?: number;
}

export function ObjectsVisualizer({
	onExplorationStart,
	showLeftPanel,
	leftPanelWidth,
}: ObjectsVisualizerProps): JSX.Element {
	const containerRef = useRef<HTMLDivElement>(null);
	const setViewportSize = useSetAtom(ViewportSizeAtom);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) {
			return;
		}
		let timerId: ReturnType<typeof setTimeout> | null = null;
		const observer = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;
			if (width > 0 && height > 0) {
				if (timerId) {
					clearTimeout(timerId);
				}
				timerId = setTimeout(() => {
					setViewportSize({ width, height });
				}, 100);
			}
		});
		observer.observe(el);
		return () => {
			if (timerId) {
				clearTimeout(timerId);
			}
			observer.disconnect();
		};
	}, [setViewportSize]);

	return (
		<div ref={containerRef} css={containerStyles}>
			<ReactFlowProvider>
				<ObjectsVisualizerInner
					onExplorationStart={onExplorationStart}
					showLeftPanel={showLeftPanel}
					leftPanelWidth={leftPanelWidth}
					containerRef={containerRef}
				/>
			</ReactFlowProvider>
		</div>
	);
}

// Re-export types for convenience
